import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DataStoreKey, DataStoreStatus } from '@compiere-ws/models/compiere-data-json';
import { ViewType } from '@iupics-components/models/view-type.enum';
import PrimeBreadcrumbComponent from '@iupics-components/overrided/prime-breadcrumb/prime-breadcrumb.component';
import { EditViewService } from '@iupics-components/standard/layouts/edit-view-ui/edit-view.service';
import {
  SmartButtonData,
  SmartButtonManagerService,
} from '@iupics-manager/managers/smart-button-manager/smart-button-manager.service';
import { ToolbarProcess } from '@iupics-manager/models/toolbar-item';
import { AbstractDynamicComponent } from '@iupics-manager/models/abstract-dynamic-component';
import { MobileNavService } from '@iupics-manager/managers/mobile-nav/mobile-nav.service';
import { UnsavedChangesService } from '@iupics-manager/managers/unsaved-changes/unsaved-changes.service';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { KeybindingDirective } from '../../../directives/keybinding.directive';

/** la menu-bar des edit-view. */
@Component({
  selector: 'iu-menu-bar-detail-ui',
  templateUrl: './menu-bar-detail-ui.component.html',
  styleUrls: ['./menu-bar-detail-ui.component.css'],
  standalone: true,
  imports: [ToolbarModule, ButtonModule, KeybindingDirective, TooltipModule, TranslateModule],
})
export default class MenuBarDetailUiComponent
  extends AbstractDynamicComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() activateKeybind = true;
  /** the record was just saved: the save button shows a check a moment */
  @Input() justSaved = false;
  /** a save is running: save button disabled with a spinner */
  @Input() saving = false;
  @Input() activeTabID: string;
  @Input() arrowsDisplayed = true;
  @Input() breadcrumb: PrimeBreadcrumbComponent;
  @Input() changingMenuBar: Subject<any>;
  @Input() isDeleteable = true;
  @Input() isInsertRecord = true;
  @Input() isReadOnly = false;
  @Input() isSplitView = false;
  @Input() isZoomTarget = false;
  @Input() nbUploadedFiles: number;
  @Input() nbRequest: number;
  @Input() noData = false;
  @Input() status: DataStoreStatus;
  @Input() processed: string;
  @Input() viewRecordChangeLogLabel: string;
  @Input() visibleButton = true;
  @Input() isZoomEditView = true;
  @Input() linkedComponents = [];
  @Input() datastorekey: DataStoreKey;
  /** AD_Window_ID of the edit view: its own smart button is not listed (same filter as iu-menu-smart-buttons-ui). */
  @Input() windowId: number;

  @Output() readonly changeGridElementEvent = new EventEmitter<any>();
  @Output() readonly changeGridViewVisibility = new EventEmitter<any>();
  @Output() readonly closeEvent = new EventEmitter<any>();
  @Output() readonly copyEvent = new EventEmitter<any>();
  @Output() readonly deleteEvent = new EventEmitter<any>();
  @Output() readonly emailEvent = new EventEmitter<any>();
  @Output() readonly exportDataEvent = new EventEmitter<any>();
  @Output() readonly joinFilesEvent = new EventEmitter<any>();
  @Output() readonly newEvent = new EventEmitter<any>();
  @Output() readonly printEvent = new EventEmitter<any>();
  @Output() readonly refreshEvent = new EventEmitter<any>();
  @Output() readonly saveEvent = new EventEmitter<any>();
  @Output() readonly undoEvent = new EventEmitter<any>();
  @Output() readonly noteEvent = new EventEmitter<any>();
  @Output() readonly requestEvent = new EventEmitter<any>();
  @Output() readonly viewRecordChangeLog = new EventEmitter<any>();
  /** Mobile "Plus" sheet: a process of the edit view was picked (same handler as the Processus dropdown). */
  @Output() readonly processEvent = new EventEmitter<ToolbarProcess>();

  showMoreActionEmitter = new EventEmitter<any>();
  isMobile = Global.isMobile();
  showMoreAction = false;
  isSync = DataStoreStatus.SYNC;

  /** Same state as the "unsaved data" confirmation of the edit view (checkData). */
  isDirty = false;
  /** Mobile device or narrow window: actions move to the bottom bar. */
  isCompact = this.isMobile;
  /** Main navigation shortcuts of the "Plus" sheet (handled by the menu top). */
  readonly mobileNav = inject(MobileNavService);
  /** Processes and smart buttons of the parent edit view (both provided by iu-edit-view-ui). */
  readonly #editViewService = inject(EditViewService, { optional: true });
  readonly #smManager = inject(SmartButtonManagerService, { optional: true });
  readonly #unsavedChanges = inject(UnsavedChangesService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #unsavedSourceId = this.#unsavedChanges.newSourceId();
  #compactQuery: MediaQueryList = null;
  readonly #onCompactChange = () => this.updateCompact();

  mailBtnCondition: boolean;
  joinFileBtnCondition: boolean;
  exportBtnCondition: boolean;
  printBtnCondition: boolean;
  closeBtnCondition: boolean;
  newBtnCondition: boolean;
  saveBtnCondition: boolean;
  refreshBtnCondition: boolean;
  deleteBtnCondition: boolean;
  copyBtnCondition: boolean;
  showMoreBtnCondition: boolean;
  noteBtnCondition: boolean;
  requestBtnCondition: boolean;

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.#compactQuery = window.matchMedia('(max-width: 640px)');
      this.#compactQuery.addEventListener('change', this.#onCompactChange);
    }
    this.updateCompact();
    this.updateButtonLists();
    this.subscriptions.push(
      this.showMoreActionEmitter.subscribe(() => {
        this.showMoreAction = !this.showMoreAction;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.status) {
      const statusChange = changes.status;
      if (statusChange.previousValue !== statusChange.currentValue) {
        this.isSync = statusChange.currentValue;
      }
      this.updateButtonLists();
    } else if (
      (changes.processed && changes.processed.previousValue !== changes.processed.currentValue) ||
      (changes.isDeleteable && changes.isDeleteable.previousValue !== changes.isDeleteable.currentValue) ||
      (changes.isInsertRecord && changes.isInsertRecord.previousValue !== changes.isInsertRecord.currentValue) ||
      (changes.isReadOnly && changes.isReadOnly.previousValue !== changes.isReadOnly.currentValue)
    ) {
      this.updateButtonLists();
    }
    this.updateDirtyState();
  }

  updateCompact(): void {
    const compact = this.isMobile || !!this.#compactQuery?.matches;
    if (compact !== this.isCompact) {
      this.isCompact = compact;
      if (!compact) {
        this.showMoreAction = false;
      }
      this.#cdr.markForCheck();
    }
  }

  /** Reports the dirty state of the current record to its workspace tab (no polling: driven by ngOnChanges). */
  updateDirtyState(): void {
    this.isDirty =
      !this.isReadOnly &&
      !this.noData &&
      (this.status === DataStoreStatus.NOTSYNC || this.status === DataStoreStatus.NEWRECORD);
    this.#unsavedChanges.setDirty(this.#unsavedSourceId, this.activeTabID, this.isDirty);
  }

  // eslint-disable-next-line complexity
  updateButtonLists(): void {
    if (this.linkedComponents) {
      const grid = this.linkedComponents.find((l) => l.viewType);
      this.arrowsDisplayed = grid ? grid.viewType === ViewType.GRID : false;
    }
    this.newBtnCondition = this.visibleButton && !this.noData && !this.isReadOnly && this.isInsertRecord;
    this.saveBtnCondition =
      this.visibleButton &&
      !this.noData &&
      ((this.processed !== 'Y' && !this.isReadOnly) || this.isSync === DataStoreStatus.NOTSYNC);
    this.refreshBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.deleteBtnCondition =
      this.visibleButton &&
      !this.noData &&
      !this.isReadOnly &&
      this.processed !== 'Y' &&
      this.isDeleteable &&
      this.isSync !== DataStoreStatus.NEWRECORD;
    this.copyBtnCondition =
      this.visibleButton &&
      !this.noData &&
      !this.isZoomEditView &&
      !this.isReadOnly &&
      this.isSync !== DataStoreStatus.NEWRECORD;
    this.mailBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.noteBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.joinFileBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.exportBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.printBtnCondition = this.visibleButton && !this.noData && this.isSync !== DataStoreStatus.NEWRECORD;
    this.closeBtnCondition = this.visibleButton && !this.noData && !this.isZoomEditView;
    this.showMoreBtnCondition = this.visibleButton && !this.noData && !this.isZoomEditView;
    this.requestBtnCondition =
      this.visibleButton &&
      !this.noData &&
      this.isSync !== DataStoreStatus.NEWRECORD &&
      this.datastorekey?.recordId?.split(',')?.length === 2 &&
      !isNaN(parseInt(this.datastorekey?.recordId?.split(',')[1], 10));
  }

  ngAfterViewInit(): void {
    // init keybind
    if (this.breadcrumb !== undefined && this.breadcrumb.model !== undefined && this.breadcrumb.model.length > 0) {
      const activeElement = this.breadcrumb.model.find((model) => model.disabled === false);
      if (activeElement !== undefined) {
        this.activateKeybind = parseInt(activeElement.id, 10) === this.tabId;
      }
    }
    // update keybind
    this.subscriptions.push(
      this.breadcrumb.breadcrumbEventEmitter.subscribe((item) => {
        if (
          item.event === IupicsTypeEvent.clickBreadCrumbItem ||
          item.event === IupicsTypeEvent.updatedActiveItemBreadcrumb
        ) {
          if (parseInt(item.tabId, 10) === this.tabId) {
            this.activateKeybind = true;
          } else {
            this.activateKeybind = false;
          }
        }
      })
    );
    setTimeout(() => {
      this.updateButtonLists();
    }, 200);
  }

  setVisibleButton(visible: boolean): void {
    this.visibleButton = visible;
  }

  /** Active edit view in compact mode: bottom bar fixed, "Plus" opens as a sheet. */
  get isFixedBar(): boolean {
    return this.isCompact && this.activateKeybind && !this.isSplitView;
  }

  /** Mobile bottom row: same arrows as the title row. */
  get recordArrowsVisible(): boolean {
    return !this.isZoomEditView && !this.isZoomTarget && this.arrowsDisplayed;
  }

  /** 0-based index of the current record in the linked grid (-1 when unknown). */
  get recordIndex(): number {
    const index = this.linkedComponents?.[0]?.GridTabInfinityScrollUiComponent?.currentSelectIndex;
    return typeof index === 'number' && index >= 0 ? index : -1;
  }

  /** On the last record of the linked grid (only when its row count is known). */
  get isLastRecord(): boolean {
    const rowCount = this.linkedComponents?.[0]?.GridTabInfinityScrollUiComponent?.grid?.api?.dataState?.()?.rowCount;
    return typeof rowCount === 'number' && rowCount > 0 && this.recordIndex >= rowCount - 1;
  }

  /** Processes of this tab, as listed by the edit view "Processus" dropdown. */
  get sheetProcesses(): ToolbarProcess[] {
    const processes = this.#editViewService?.processes() ?? [];
    return this.tabId ? processes.filter((process) => process.tabId === this.tabId) : [];
  }

  /** Smart buttons, as listed by iu-menu-smart-buttons-ui (visible and overflow ones). */
  get sheetSmartButtons(): SmartButtonData[] {
    return this.#smManager?.registration.data()?.filter((b) => !b.isOpened && b.key !== this.windowId) ?? [];
  }

  toggleSmartButton(key: number): void {
    this.#smManager?.toggleSmartButton(key);
  }

  onChildUpdate(event): void {}

  onSiblingUpdate(event: IupicsEvent): void {}

  onRemoveComponent(event: IupicsEvent): void {}

  ngOnDestroy(): void {
    this.#compactQuery?.removeEventListener('change', this.#onCompactChange);
    this.#unsavedChanges.setDirty(this.#unsavedSourceId, null, false);
    super.ngOnDestroy();
  }
}
